import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { trackConversion } from "@/lib/gtag";
import { BookingPanel } from "@/components/BookingPanel";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { MobileHeroCta } from "@/components/MobileHeroCta";
import { IdleCallNudge } from "@/components/IdleCallNudge";
import { TrustRow } from "@/components/TrustRow";
import {
  Clock,
  UserRound,
  Car,
  ShieldCheck,
  Plane,
  Briefcase,
  MapPin,
  Headphones,
  Phone,
  Calendar,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Star,
} from "lucide-react";
import heroSedan from "@/assets/hero-sedan.webp";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Melbourne Taxi & Airport Transfers | SE Premium Cabs" },
      {
        name: "description",
        content:
          "Melbourne taxi & airport transfers with fixed upfront fares. Sedan, SUV and Maxi cabs, corporate travel, booked online or by phone 24/7.",
      },
      { property: "og:title", content: "Melbourne Taxi & Airport Transfers | SE Premium Cabs" },
      {
        property: "og:description",
        content:
          "Premium chauffeured cabs across Melbourne. Fixed-price airport transfers, corporate travel & 24/7 bookings. Sedan, SUV & Maxi options.",
      },
      { property: "og:url", content: "https://sepremiumcabs.com/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://sepremiumcabs.com/" },
      { rel: "preload", as: "image", href: heroSedan, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://sepremiumcabs.com/#business",
          name: "SE Premium Cabs",
          image: "https://sepremiumcabs.com/favicon.png",
          url: "https://sepremiumcabs.com/",
          telephone: "+61493100265",
          email: "sepremiumcabs@gmail.com",
          priceRange: "$$",
          currenciesAccepted: "AUD",
          paymentAccepted: "Cash, EFTPOS, Credit Card",
          geo: { "@type": "GeoCoordinates", latitude: -37.9871, longitude: 145.2149 },
          areaServed: [
            "Melbourne, Victoria, Australia",
            "Melbourne Airport (Tullamarine)",
            "Dandenong",
            "Berwick",
            "Narre Warren",
            "Frankston",
            "Springvale",
            "Clayton",
            "Box Hill",
            "Glen Waverley",
            "Melbourne CBD",
          ],
          serviceArea: {
            "@type": "GeoCircle",
            geoMidpoint: { "@type": "GeoCoordinates", latitude: -37.9871, longitude: 145.2149 },
            geoRadius: "60000",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Cab & chauffeur services",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Melbourne Airport transfers" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Maxi cab (11 seater) hire" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Corporate chauffeur service" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Point-to-point suburban cabs" } },
            ],
          },
          address: {
            "@type": "PostalAddress",
            addressLocality: "Melbourne",
            addressRegion: "VIC",
            addressCountry: "AU",
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
              opens: "00:00",
              closes: "23:59",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How much is a taxi from Melbourne Airport to the CBD?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A premium sedan from Melbourne Airport (Tullamarine) to the Melbourne CBD is typically a fixed fare with no surge pricing. Call 0493 100 265 or book online for an upfront quote before you travel.",
              },
            },
            {
              "@type": "Question",
              name: "Do you provide 24/7 airport transfers?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. SE Premium Cabs operates 24 hours a day, 7 days a week including public holidays. We track your flight and adjust pickup times automatically for early arrivals or delays.",
              },
            },
            {
              "@type": "Question",
              name: "Which suburbs of Melbourne do you cover?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We serve Melbourne Airport, the South East suburbs (Dandenong, Berwick, Frankston, Pakenham, Cranbourne and more), and the Eastern suburbs (Box Hill, Ringwood, Doncaster, Glen Waverley and more). See our Suburbs page for the full list.",
              },
            },
            {
              "@type": "Question",
              name: "What vehicles are available?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Premium Sedan (up to 4 passengers, 3 bags), Premium SUV (up to 6 passengers, 5 bags), and Maxi (up to 11 passengers, 9 bags). Child seats available on request.",
              },
            },
            {
              "@type": "Question",
              name: "How do I pay?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We accept cash, credit card (Visa, Mastercard, AMEX), EFTPOS and corporate accounts. Payment is taken at the end of your trip unless a corporate account is set up.",
              },
            },
            {
              "@type": "Question",
              name: "Can I book in advance?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Use the booking form on our website or call 0493 100 265 to schedule a ride hours, days or weeks in advance. We recommend booking at least 2 hours before airport pickups.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster />
      <SiteHeader active="home" />
      <main className="mx-auto max-w-[1400px] px-6 py-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
          <div id="book" className="scroll-mt-24 lg:order-2">
            <BookingPanel />
          </div>
          <div className="lg:order-1">
            <HeroSection />
          </div>
        </div>
        <TrustRow />
        <ServicesRow />
        <TestimonialsSection />
        <FaqSection />
      </main>
      <SiteFooter />
      <div className="h-16 lg:hidden" />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}

function HeroSection() {
  const features = [
    { icon: Clock, title: "24/7", sub: "SERVICE" },
    { icon: UserRound, title: "PROFESSIONAL", sub: "DRIVERS" },
    { icon: Car, title: "LUXURY", sub: "VEHICLES" },
    { icon: ShieldCheck, title: "SAFE &", sub: "RELIABLE" },
  ];
  return (
    <section className="relative overflow-hidden rounded-xl bg-card">
        <img
          src={heroSedan}
          alt="Luxury black sedan on Melbourne bridge at night"
          width={1280}
          height={720}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-transparent" />
        <div className="relative z-10 px-10 py-16 lg:py-24 max-w-2xl">
          <h1 className="font-serif text-5xl font-bold leading-tight tracking-tight lg:text-6xl">
            MELBOURNE TAXI
            <br />
            <span className="text-primary">& AIRPORT TRANSFERS</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            Premium cabs across Melbourne, 24/7.
            <br />
            Fixed-fare airport transfers, corporate and long-distance travel.
          </p>
          <MobileHeroCta />
          <div className="mt-10 grid grid-cols-4 gap-4 max-w-md">
            {features.map((f) => (
              <div key={f.title} className="text-center">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-primary/40 text-primary">
                  <f.icon className="h-6 w-6" />
                </div>
                <div className="mt-3 text-[10px] font-bold tracking-wider leading-tight">
                  {f.title}
                  <br />
                  {f.sub}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 hidden flex-wrap gap-4 lg:flex">
            <a
              href="#book"
              className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground transition-transform hover:scale-[1.02]"
            >
              <Calendar className="h-4 w-4" />
              BOOK A RIDE
            </a>
            <Link
              to="/melbourne-airport-taxi"
              className="flex items-center gap-2 rounded-md border border-primary/60 px-6 py-3 text-sm font-bold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              <Plane className="h-4 w-4" />
              MELBOURNE AIRPORT TRANSFERS
            </Link>
            <a
              href="tel:0493100265"
              onClick={() => trackConversion("phoneCall")}
              className="flex items-center gap-2 rounded-md border border-primary px-6 py-3 text-sm font-bold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              <Phone className="h-4 w-4" />
              CALL 0493 100 265
            </a>
          </div>
        </div>
    </section>
  );
}

function ServicesRow() {
  const services = [
    {
      icon: Plane,
      title: "MELBOURNE AIRPORT TRANSFERS",
      desc: "Fixed-fare pickup and drop-off at Tullamarine, with flight tracking.",
      to: "/melbourne-airport-taxi" as const,
    },
    {
      icon: Briefcase,
      title: "CORPORATE TAXI",
      desc: "Business travel on account, with monthly invoicing and upfront fares.",
      to: "/corporate-taxi" as const,
    },
    {
      icon: MapPin,
      title: "LOCAL & SUBURB TRIPS",
      desc: "Short trips and day work across the south east and eastern suburbs.",
      to: "/suburbs" as const,
    },
    {
      icon: Headphones,
      title: "24/7 BOOKINGS & SUPPORT",
      desc: "Call, WhatsApp or book online any hour, any day of the year.",
      to: "/contact" as const,
    },
  ];
  return (
    <section className="rounded-xl border border-border/60 bg-card p-8">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <Link
              key={s.title}
              to={s.to}
              className="group rounded-lg p-2 text-center transition-colors hover:bg-secondary/40"
            >
              <s.icon className="mx-auto h-8 w-8 text-primary" />
              <h3 className="mt-4 text-sm font-bold tracking-wider group-hover:text-primary">
                {s.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {s.desc}
              </p>
            </Link>
          ))}
        </div>
    </section>
  );
}


function TestimonialsSection() {
  const reviews = [
    {
      quote:
        "Excellent service! On time, clean car and very professional driver. Highly recommended.",
      name: "Michael R.",
      date: "April 2025",
    },
    {
      quote:
        "Booked a maxi cab for our 4am airport run from Berwick. Driver arrived early, helped with all the luggage and the fixed price was exactly as quoted.",
      name: "Priya S.",
      date: "March 2025",
    },
    {
      quote:
        "We use SE Premium Cabs for all our corporate pickups now. Immaculate sedans, discreet chauffeurs and never once late.",
      name: "Daniel W.",
      date: "March 2025",
    },
    {
      quote:
        "Flight landed two hours late and the driver still tracked it and was waiting at the gate. Zero stress after a long haul.",
      name: "Amanda T.",
      date: "February 2025",
    },
    {
      quote:
        "Great value compared to rideshare surge pricing. Comfortable SUV, friendly driver and easy online booking.",
      name: "Hassan K.",
      date: "January 2025",
    },
    {
      quote:
        "Sent Mum home from Clayton at midnight and felt completely safe. The driver called ahead and texted when he arrived. Five stars.",
      name: "Jessica L.",
      date: "December 2024",
    },
  ];
  const [index, setIndex] = useState(0);
  const current = reviews[index]!;
  const go = (dir: number) =>
    setIndex((i) => (i + dir + reviews.length) % reviews.length);

  return (
    <section className="flex flex-col items-center justify-center rounded-xl border border-border/60 bg-card p-10 text-center">
      <h2 className="font-serif text-2xl font-bold tracking-tight">
        WHAT OUR CLIENTS SAY
      </h2>
      <div className="mt-2 h-px w-16 bg-primary" />
      <div className="mt-8 flex items-center gap-6">
        <button onClick={() => go(-1)} aria-label="Previous testimonial" className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="max-w-md min-h-[7rem]">
          <p className="text-base italic leading-relaxed">"{current.quote}"</p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <div className="flex gap-0.5 text-primary">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">
              — {current.name} · {current.date} · Google review
            </span>
          </div>
        </div>
        <button onClick={() => go(1)} aria-label="Next testimonial" className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-4 flex">
        {reviews.map((r, i) => (
          <button
            key={r.name}
            onClick={() => setIndex(i)}
            aria-label={`Show review ${i + 1}`}
            className="grid h-6 w-6 place-items-center"
          >
            <span
              className={`block h-2 w-2 rounded-full transition-colors ${i === index ? "bg-primary" : "bg-border"}`}
            />
          </button>
        ))}
      </div>
    </section>
  );
}

const FAQS: { q: string; a: string }[] = [
  {
    q: "How much is a taxi from Melbourne Airport to the CBD?",
    a: "A premium sedan from Melbourne Airport (Tullamarine) to the Melbourne CBD is a fixed fare with no surge pricing. Call 0493 100 265 or book online for an upfront quote before you travel.",
  },
  {
    q: "Do you provide 24/7 airport transfers?",
    a: "Yes. SE Premium Cabs operates 24 hours a day, 7 days a week including public holidays. We track your flight and adjust pickup times automatically for early arrivals or delays.",
  },
  {
    q: "Which suburbs of Melbourne do you cover?",
    a: "We serve Melbourne Airport, the South East suburbs (Dandenong, Berwick, Frankston, Pakenham, Cranbourne and more), and the Eastern suburbs (Box Hill, Ringwood, Doncaster, Glen Waverley and more). See our Suburbs page for the full list.",
  },
  {
    q: "What vehicles are available?",
    a: "Premium Sedan (up to 4 passengers, 3 bags), Premium SUV (up to 6 passengers, 5 bags), and Maxi (up to 11 passengers, 9 bags). Child seats available on request.",
  },
  {
    q: "How do I pay?",
    a: "We accept cash, credit card (Visa, Mastercard, AMEX), EFTPOS and corporate accounts. Payment is taken at the end of your trip unless a corporate account is set up.",
  },
  {
    q: "Can I book in advance?",
    a: "Yes. Use the booking form on our website or call 0493 100 265 to schedule a ride hours, days or weeks in advance. We recommend booking airport pickups at least 2 hours ahead.",
  },
];

function FaqSection() {
  return (
    <section id="faq" className="rounded-xl border border-border/60 bg-card p-8 lg:p-12">
      <div className="flex items-center gap-3">
        <HelpCircle className="h-6 w-6 text-primary" />
        <h2 className="font-serif text-3xl font-bold tracking-tight">FREQUENTLY ASKED QUESTIONS</h2>
      </div>
      <div className="mt-2 h-px w-16 bg-primary" />
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {FAQS.map((f) => (
          <details
            key={f.q}
            className="group rounded-lg border border-border/60 bg-background p-5 open:border-primary/60"
          >
            <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold text-foreground">
              <span>{f.q}</span>
              <span className="text-primary transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
